import { useCallback, useRef, useState } from 'react';

import { useConfigValue } from 'hooks/configs/use-config-value';
import { isProduction } from 'utils/environment';

import { ICashApp, ICashAppResult } from '../types';
import { getQueryParams, loadScript, removeIsRedirectedParam } from '../utils';

import { CASHAPP_BUTTON_ID } from './cashapp-button.styled';
import { useCashAppOnCompleteHelper } from './useCashAppOnCompleteHelper';

declare global {
  interface Window {
    CashApp?: unknown;
  }
}

const SCRIPTS = [
  {
    id: 'afterpay-button',
    url: isProduction
      ? 'https://static.afterpay.com/afterpay-button.js'
      : 'https://static-us.afterpay.com/javascript/button/afterpay-button.js',
  },
];

async function embedScripts(): Promise<void> {
  for (const script of SCRIPTS) {
    await loadScript(script.id, script.url);
  }
}

export const useCashApp = ({ onResult, onError }: ICashApp) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  const { onComplete } = useCashAppOnCompleteHelper({ onResult });
  const amountRef = useRef<number>(0);

  const { cashAppMerchantId } = useConfigValue({
    key: 'firstData',
    defaultValue: {},
  });

  const createAfterpayButton = useCallback(() => {
    const existingButton = document.querySelector('afterpay-button');
    if (existingButton) {
      return existingButton;
    }

    // Create the afterpay-button element
    const afterpayButton = document.createElement('afterpay-button');

    // Set attributes
    afterpayButton.setAttribute('isCashAppPay', 'true');
    afterpayButton.setAttribute('merchantPublicKey', cashAppMerchantId);

    // Append the button directly to the body
    document.body.appendChild(afterpayButton);
    return afterpayButton;
  }, [cashAppMerchantId]);

  const initialize = useCallback(
    async (
      afterpayButton: HTMLElement & {
        initializeCashAppPayListeners: Function;
        initializeForCashAppPay: Function;
        renderCashAppPayButton: Function;
        restartCashAppPay: Function;
      },
      amount: number,
      isRedirected = false
    ) => {
      afterpayButton.restartCashAppPay();

      const handleError = (event: any) => {
        setIsLoading(false);
        onError(new Error(`Could not complete Cash App payment: ${event}`));
      };

      function bindAttributesToButton(button: any, amount: number, currency = 'USD') {
        button.setAttribute('amount', String(amount));
        button.setAttribute('currency', currency);
        button.setAttribute('merchantPublicKey', cashAppMerchantId);
      }

      const redirectConfirmUrl = `${window.location.origin}${window.location.pathname}`;

      const button = document.getElementById(CASHAPP_BUTTON_ID);
      let handler: any = null;

      const wrappedOnComplete = (cardInfo: { data: ICashAppResult }) => {
        // handler can't be reused. wrappedOnComplete will be called if final confirm is cancelled
        button?.removeEventListener('click', handler);

        // handles cancelling out of confirm order page and allowing us to go back in to it
        // since the event handler for cash app is now gone from redirect
        button?.addEventListener('click', async (event: any) => {
          event.preventDefault();
          setIsLoading(true);
          onComplete(cardInfo).finally(() => setIsLoading(false));
        });

        return onComplete(cardInfo).finally(() => setIsLoading(false));
      };

      const cashAppOptions = {
        countryCode: 'US',
        button: false,
        manage: false,
        redirectConfirmUrl,
        onError: handleError,
        onComplete: wrappedOnComplete,
        eventListeners: {
          CUSTOMER_REQUEST_DECLINED: () => {
            document.body.removeChild(afterpayButton);
            removeIsRedirectedParam();
            const resetAfterpayButton = createAfterpayButton();
            afterpayButton.restartCashAppPay();
            initialize(resetAfterpayButton as any, amountRef.current, false);
            setIsLoading(false);
            setIsDeclined(true);
          },
          CUSTOMER_REQUEST_FAILED: () => {
            setIsDeclined(true);
          },
        },
        onBegin: ({ begin }: any) => {
          handler = async (event: any) => {
            event.preventDefault();

            bindAttributesToButton(afterpayButton, amount);
            afterpayButton.initializeForCashAppPay({
              redirectConfirmUrl,
              onError: handleError,
              onComplete: wrappedOnComplete,
            });
            begin();
          };
          button?.addEventListener('click', handler);
        },
      };

      if (isRedirected) {
        setIsDeclined(false);
        return afterpayButton.initializeCashAppPayListeners({
          countryCode: 'US',
          cashAppPayListenerOptions: cashAppOptions,
        });
      }

      if (!button) {
        return;
      }

      return afterpayButton.renderCashAppPayButton({
        countryCode: 'US',
        cashAppPayButtonOptions: cashAppOptions,
      });
    },
    [cashAppMerchantId, createAfterpayButton, onComplete, onError]
  );

  const loadCashAppButton = useCallback(
    async ({ amount }: { amount: number }): Promise<void> => {
      amountRef.current = amount;
      setIsLoading(true);

      if (!window.CashApp) {
        await embedScripts();
      }

      const afterpayButton = createAfterpayButton();
      const queryParams = getQueryParams();
      const isRedirected = (queryParams as any)?.isRedirected === 'true';
      await initialize(afterpayButton as any, amount, isRedirected);

      if (!isRedirected) {
        setIsLoading(false);
      }
    },
    [createAfterpayButton, initialize]
  );

  return {
    isLoading,
    isDeclined,
    loadCashAppButton,
  };
};
